import { auth } from "@lib/firebase";
import {
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getRedirectResult,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  verifyPasswordResetCode,
  type ParsedToken as AuthClaims,
  type User,
} from "firebase/auth";
/**
 * Checks if a user is currently logged in
 * @returns boolean indicating if user is logged in
 */
export const isLoggedIn = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;

  try {
    // Get the ID token to check for custom claims
    const token = await user.getIdTokenResult();
    // Check if the user has an orgId claim
    return !!token && token.claims.orgId !== undefined;
  } catch (error) {
    console.error("Error checking authentication status:", error);
    return false;
  }
};

/**
 * Signs in a user with email and password
 * @param email User's email
 * @param password User's password
 * @returns Promise resolving to the User object
 */
export const loginWithEmailPassword = async (
  email: string,
  password: string,
): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    return userCredential.user;
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

/**
 * Signs in a user with a custom token
 * @param token - The custom token to sign in with
 * @returns Promise resolving to the User object
 */
export const handleSignInWithCustomToken = async (
  token: string,
): Promise<User> => {
  const userCredential = await signInWithCustomToken(auth, token);
  return userCredential.user;
};

/**
 * Creates a new user with email and password
 * @param email User's email
 * @param password User's password
 * @returns Promise resolving to the User object
 */
export const signupWithEmailPassword = async (
  email: string,
  password: string,
): Promise<User> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    return userCredential.user;
  } catch (error) {
    console.error("Signup error:", error);
    throw error;
  }
};

/**
 * Signs in a user with Google
 * @returns Promise resolving to the User object
 */
export const loginWithGoogle = async (): Promise<User> => {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    return userCredential.user;
  } catch (error) {
    console.error("Google login error:", error);
    throw error;
  }
};

/**
 * Signs in a user with a Google ID token obtained outside the app
 * (e.g. the One Tap prompt on the marketing site).
 * @param idToken Google ID token (the One Tap `credential` JWT)
 * @returns Promise resolving to the User object
 */
export const loginWithGoogleCredential = async (
  idToken: string,
): Promise<User> => {
  try {
    const credential = GoogleAuthProvider.credential(idToken);
    const userCredential = await signInWithCredential(auth, credential);
    return userCredential.user;
  } catch (error) {
    console.error("Google credential login error:", error);
    throw error;
  }
};

/**
 * Signs in a user with Microsoft
 * @returns Promise resolving to the User object
 */
export const loginWithMicrosoft = async (): Promise<User> => {
  try {
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({ prompt: "select_account" });
    const userCredential = await signInWithPopup(auth, provider);
    return userCredential.user;
  } catch (error) {
    console.error("Microsoft login error:", error);
    throw error;
  }
};

/**
 * Refreshes the authentication token for the current user
 * @returns Promise resolving to an object with the refreshed token and claims, or null if no user is logged in
 */
export const refreshAuthToken = async (): Promise<{
  token: string;
  claims: AuthClaims;
} | null> => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.log("No user is currently logged in");
      return null;
    }

    // Force token refresh
    const token = await currentUser.getIdToken(true);

    // Retrieve the claims
    const decodedToken = await currentUser.getIdTokenResult();
    const claims = decodedToken.claims;

    return { token, claims };
  } catch (error) {
    console.error("Token refresh error:", error);
    throw error;
  }
};

/**
 * Initiates SAML SSO sign-in with the specified domain.
 * @param domain - The SAML provider domain (e.g., "example.com").
 * @returns A Promise that resolves when the redirect is initiated.
 */
export const signInWithSAMLProvider = async (domain: string) => {
  const provider = new SAMLAuthProvider(`saml.${domain}`);
  return signInWithRedirect(auth, provider);
};

/**
 * Completes a pending redirect sign-in (e.g. SAML SSO) after the identity
 * provider redirects back to the app. Must be called once on app/login mount.
 *
 * - Resolves to the signed-in `User` when a redirect was just completed.
 * - Resolves to `null` when there is no pending redirect (the common case).
 * - Rejects with a FirebaseError when the redirect failed on the return leg
 *   (e.g. storage partitioning, expired/invalid nonce). Without this call the
 *   `signInWithRedirect` flow never surfaces those errors, since the original
 *   call site is gone after the full-page redirect.
 *
 * Note: this cannot catch IdP-initiated logins (user launches from the IdP's
 * portal). In that flow the assertion is POSTed straight to Firebase's default
 * `__/auth/handler` and the user never returns to our origin, so this never runs.
 * @returns A Promise resolving to the signed-in User, or null if none pending.
 */
export const getSAMLRedirectResult = async (): Promise<User | null> => {
  const result = await getRedirectResult(auth);
  return result?.user ?? null;
};

/**
 * Verifies a password reset code.
 * @param code - The password reset code from the email link.
 * @returns A Promise that resolves to the user's email address if the code is valid.
 */
export const verifyPasswordReset = async (code: string) => {
  return verifyPasswordResetCode(auth, code);
};

/**
 * Confirms a password reset with the provided code and new password.
 * @param code - The password reset code from the email link.
 * @param password - The new password to set.
 * @returns A Promise that resolves when the password has been reset.
 */
export const resetPassword = async (code: string, password: string) => {
  return confirmPasswordReset(auth, code, password);
};

/**
 * Retrieves custom authentication claims for the current user.
 * @returns A Promise that resolves to the user's custom claims, or an empty object if not logged in.
 */
export const getCustomClaims = async (): Promise<AuthClaims> => {
  const currentUser = auth.currentUser;
  if (!currentUser) return {};
  const token = await currentUser.getIdTokenResult();
  return token.claims;
};

/**
 * Retrieves the current user's authentication token as a Bearer token.
 * @returns A Promise that resolves to the Bearer token string, or an empty string if not logged in.
 */
export const getAuthToken = async (): Promise<string> => {
  const currentUser = auth.currentUser;
  if (!currentUser) return "";
  const token = await currentUser.getIdToken();
  return `Bearer ${token}`;
};

/**
 * Signs out the current user
 * @returns A Promise that resolves when the user has been signed out.
 */
export const signOut = async () => {
  await auth.signOut();
};

/**
 * Retrieves the user's sign up type
 * @returns The user's sign up type
 */
export const getUserSignUpType = () => {
  const user = auth.currentUser;
  if (!user) return null;
  return user.providerData[0]?.providerId;
};
